import { Box, Button, Container, IconButton, Stack, Typography } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { MaterialReactTable, MRT_ColumnFiltersState } from "material-react-table";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../contexts/UserContext";
import FundingAuthorizationActions from "../FundingAuthorizationActions";
import { FundingAuthorizationContext } from "../FundingAuthorizationContext";
import { FundingPackageStatus, isMadexOrder } from "../types";
import { getColumns } from "./columns";
import useResponsive from "../../../minimals/hooks/useResponsive";
import MobileFundingPackages from "../MobileFundingPackages";

export default function ReviewFunding() {
  const { user } = useContext(UserContext);
  const isMobile = useResponsive("down", "md");
  const { dateRange, setDateRange, assignedToMe, setAssignedToMe, tableRef, assignedToUser, fundingPackages, newRows, fedReferences, madexOnly, loading } =
    useContext(FundingAuthorizationContext);
  const [managerDepartments, setManagerDepartments] = useState<string[]>([]);
  const queryParams = new URLSearchParams(location.search);
  const { orderNumber } = useParams<{ orderNumber?: string }>();
  const assignedToId = queryParams.get("assignedtoid");
  const isUnassignedOnly = Number(assignedToId) == -1;

  const [enableOrdering, setEnableOrdering] = useState(false);
  const [tableData, setTableData] = useState<any[]>([fundingPackages]);
  const [search, setSearch] = useState("");

  const getDefaultFilters = (): MRT_ColumnFiltersState => {
    const filters: MRT_ColumnFiltersState = [];
    if (orderNumber) {
      filters.push({
        id: "orderNumber",
        value: orderNumber
      });
    }
    return filters;
  };
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(getDefaultFilters());
  const [filtersModified, setFiltersModified] = useState(false);
  const defaultFiltersRef = useRef(getDefaultFilters());

  const handleColumnFiltersChange = useCallback(
    (updaterOrValue: MRT_ColumnFiltersState | ((prev: MRT_ColumnFiltersState) => MRT_ColumnFiltersState)) => {
      setColumnFilters((prev) => {
        const next = typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        const defaultFilters = defaultFiltersRef.current;
        const isDefault =
          next.length === defaultFilters.length &&
          defaultFilters.every((df) => next.some((nf) => nf.id === df.id && JSON.stringify(nf.value) === JSON.stringify(df.value)));
        setFiltersModified(!isDefault);
        return next;
      });
    },
    []
  );
  const getAndSetManagerDepartmentsByEmail = async (emailAddress: string) => {
    const { data } = await axios.get<string[]>(
      `/api/outstandingchecks/getManagerDepartmentsByEmail?emailAddress=${emailAddress}`
    );
    setManagerDepartments(data);
  };

  const toggleColumnOrdering = () => {
    setEnableOrdering(!enableOrdering);
  };

  useEffect(() => {
    if (user?.email) {
      getAndSetManagerDepartmentsByEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    setTableData(tableData);
  }, [tableData]);

  useEffect(() => {
    if (orderNumber) {
      setColumnFilters([{ id: "orderNumber", value: orderNumber }]);
    }
  }, [orderNumber]);
  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };

  const columns = getColumns(fedReferences);

  const sortedFundingPackages = useMemo(() => {
    return [...fundingPackages].sort((a, b) => {
      const aIsNew = newRows.has(a.id) ? 0 : 1;
      const bIsNew = newRows.has(b.id) ? 0 : 1;
      if (aIsNew !== bIsNew) return aIsNew - bIsNew;
      return (a.priority ?? Infinity) - (b.priority ?? Infinity);
    });
  }, [fundingPackages, newRows]);

  const preFilteredPackages = useMemo(() => {
    if (filtersModified) return sortedFundingPackages;

    const threeDaysAgo = dayjs().subtract(3, "days");
    return sortedFundingPackages.filter((pkg) => {
      if (pkg.status === FundingPackageStatus.Requested) return true;
      if (pkg.status === FundingPackageStatus.Deficient && pkg.deficientDate) {
        return dayjs(pkg.deficientDate).isAfter(threeDaysAgo);
      }
      return false;
    });
  }, [sortedFundingPackages, filtersModified]);
  const visiblePackages = useMemo(
    () => (madexOnly ? preFilteredPackages.filter((p) => isMadexOrder(p.orderNumber)) : preFilteredPackages),
    [preFilteredPackages, madexOnly]
  );

  return (
    <>
      <Helmet>
        <title> Funding Packages | TitleQ</title>
      </Helmet>
      <Container maxWidth="xl">
        {isMobile ? (
          <MobileFundingPackages
            title="Wire Room Queue"
            loading={loading}
            packages={preFilteredPackages}
            columns={columns}
            columnFilters={columnFilters}
            onColumnFiltersChange={handleColumnFiltersChange}
            columnVisibility={{ createdByEmail: true }}
          />
        ) : (
          <>
            <Typography variant="h3">Wire Room Queue</Typography>
            <Typography variant="body2" color="text.secondary">
              Review and approve incoming wire requests.
            </Typography>
            <Stack direction="row" justifyContent="space-between" mt={4}>
              <Typography variant="h4" marginBottom={2}>
                Package History
                {isUnassignedOnly && " - Unassigned"}
                {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
              </Typography>
            </Stack>
            <MaterialReactTable
              enableStickyHeader
              columns={columns}
              data={visiblePackages}
              state={{ columnFilters }}
              onColumnFiltersChange={handleColumnFiltersChange}
              enableGlobalFilter
              enableFacetedValues
              enableColumnOrdering={enableOrdering}
              enableRowSelection
              autoResetPageIndex={false}
              globalFilterFn="includesString"
              initialState={{
                density: "compact",
                pagination: { pageIndex: 0, pageSize: 30 },
                columnVisibility: {
                  createdByEmail: true
                },
                showColumnFilters: true
              }}
              // Leaves room for the page header, toolbars and main's bottom padding so only the table scrolls
              muiTableContainerProps={{ sx: { maxHeight: { xs: "calc(100vh - 448px)", lg: "calc(100vh - 372px)" } } }}
              muiTablePaperProps={{ sx: { border: (theme) => `1px solid ${theme.palette.grey[400]}` } }}
              muiTableBodyProps={{
                sx: {
                  "& .MuiTableRow-root:hover .MuiTableCell-root": {
                    backgroundColor: "inherit !important"
                  }
                }
              }}
              muiTableBodyRowProps={({ row }) => ({
                sx: {
                  backgroundColor: newRows.has(row.original.id) ? "#E9FCD4" : "#fff"
                }
              })}
              renderTopToolbarCustomActions={({ table }) => {
                return (
                  <Box sx={{ flexGrow: 1, position: "relative" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <FundingAuthorizationActions
                        dateRange={dateRange}
                        setDateRange={setDateRange}
                        assignedToMe={assignedToMe}
                        setAssignedToMe={setAssignedToMe}
                        table={table}
                        hideAssignedToMeOptions={!!assignedToId}
                        onRowsDeselected={onRowsDeselected}
                      />
                    </Stack>
                  </Box>
                );
              }}
            />
          </>
        )}
      </Container>
    </>
  );
}
